import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router-dom'
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import {
  Alert,
  Box,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material'
import {
  CommonButton,
  CommonDialog,
  CommonTextField,
} from '@/components/common'
import {
  DRIVE_DONORS_EMAIL_MODES,
  DRIVE_DONORS_EMAIL_SOURCE,
  DRIVE_DONORS_RECIPIENT_MODES,
  buildDriveDonorsEmailComposePath,
  buildDriveDonorsPath,
  getDriveDonorsDeliveryCounts,
  getDriveDonorsEmailSteps,
  getDriveDonorsSelectedCount,
} from '@/components/chairperson/drive-donors/constants'
import { DriveDonorsRecipientsDialog } from '@/components/chairperson/drive-donors/DriveDonorsRecipientsDialog'
import { chairpersonService } from '@/services'
import { pickChairpersonDriveNavState } from '@/utils/chairpersonNavigation'

const EMPTY_DONOR_IDS = []
const EMPTY_GUEST_IDS = []

export const ChairpersonDriveDonorsReviewSendPage = () => {
  const { driveId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { selectedAccountId } = useOutletContext()

  const {
    sourceTab,
    recipientMode = DRIVE_DONORS_RECIPIENT_MODES.SELECTED,
    templateId,
    templateName,
    emailMode: stateEmailMode,
    subject,
    emailBody,
    recipientPreview,
    composeContext,
    accountId: stateAccountId,
  } = location.state ?? {}

  const donorIds = useMemo(
    () => location.state?.donorIds ?? EMPTY_DONOR_IDS,
    [location.state?.donorIds]
  )
  const guestIds = useMemo(
    () => location.state?.guestIds ?? EMPTY_GUEST_IDS,
    [location.state?.guestIds]
  )

  const accountId = stateAccountId ?? selectedAccountId
  const emailMode =
    stateEmailMode ??
    composeContext?.ds_templates?.find(
      template => String(template.email_id) === String(templateId)
    )?.email_mode ??
    DRIVE_DONORS_EMAIL_MODES.COMPOSED
  const isPreconfigured = emailMode === DRIVE_DONORS_EMAIL_MODES.PRECONFIGURED
  const donorsPath = buildDriveDonorsPath(driveId)
  const composePath = buildDriveDonorsEmailComposePath(driveId)
  const driveNavState = useMemo(
    () => pickChairpersonDriveNavState(location.state),
    [
      location.state?.returnTo,
      location.state?.fromTab,
      location.state?.driveName,
      location.state?.driveDate,
      location.state?.accountId,
    ]
  )

  const [preview, setPreview] = useState(recipientPreview)
  const [loadingPreview, setLoadingPreview] = useState(!recipientPreview)
  const previewLoadedRef = useRef(Boolean(recipientPreview))
  const [sending, setSending] = useState(false)
  const [showRecipientsDialog, setShowRecipientsDialog] = useState(false)
  const [showSuccessDialog, setShowSuccessDialog] = useState(false)
  const [showDiscardDialog, setShowDiscardDialog] = useState(false)
  const [showTestEmailDialog, setShowTestEmailDialog] = useState(false)
  const [testEmail, setTestEmail] = useState('')
  const [sendingTestEmail, setSendingTestEmail] = useState(false)
  const [sendError, setSendError] = useState('')
  const [sendResult, setSendResult] = useState(null)

  useEffect(() => {
    const requiresSelection =
      recipientMode === DRIVE_DONORS_RECIPIENT_MODES.SELECTED
    const missingSelection =
      requiresSelection && !donorIds.length && !guestIds.length
    const missingTemplate = !templateId
    const missingComposedFields = !isPreconfigured && (!subject || !emailBody)

    if (
      !sourceTab ||
      missingSelection ||
      missingTemplate ||
      missingComposedFields
    ) {
      navigate(composePath, {
        replace: true,
        state: location.state,
      })
    }
  }, [
    donorIds.length,
    guestIds.length,
    sourceTab,
    templateId,
    subject,
    emailBody,
    recipientMode,
    isPreconfigured,
    navigate,
    composePath,
  ])

  useEffect(() => {
    const refreshPreview = async () => {
      if (previewLoadedRef.current || !accountId) return

      setLoadingPreview(true)
      try {
        const response =
          await chairpersonService.previewDriveDonorsEmailRecipients(
            driveId,
            accountId,
            {
              donor_ids: donorIds,
              guest_ids: guestIds,
              source_tab: sourceTab,
              source: DRIVE_DONORS_EMAIL_SOURCE,
              recipient_mode: recipientMode,
            }
          )
        if (response?.status_code === 200) {
          setPreview(response.data)
          previewLoadedRef.current = true
        }
      } catch (error) {
        console.error('Failed to refresh recipient preview', error)
      } finally {
        setLoadingPreview(false)
      }
    }

    refreshPreview()
  }, [driveId, accountId, donorIds, guestIds, sourceTab, recipientMode])

  const accountName =
    preview?.drive?.account_name ?? composeContext?.drive?.account_name ?? ''
  const selectedCount =
    recipientMode === DRIVE_DONORS_RECIPIENT_MODES.SELECTED
      ? getDriveDonorsSelectedCount({ donorIds, guestIds })
      : Number(preview?.total_selected) || 0
  const recipientModeLabel =
    recipientMode === DRIVE_DONORS_RECIPIENT_MODES.ALL_DONORS
      ? 'all donors'
      : recipientMode === DRIVE_DONORS_RECIPIENT_MODES.ALL_ELIGIBLE_DONORS
        ? 'all eligible donors'
        : 'selected donors'
  const { willReceive, willSkip } = getDriveDonorsDeliveryCounts(preview)
  const stepLabels = getDriveDonorsEmailSteps(emailMode)
  const activeStep = stepLabels.length - 1

  const handleBack = () => {
    navigate(composePath, {
      state: {
        ...driveNavState,
        donorIds,
        guestIds,
        sourceTab,
        recipientMode,
        accountId,
        templateId,
        templateName,
        emailMode,
        subject,
        emailBody,
        recipientPreview: preview,
        composeContext,
      },
    })
  }

  const handleCancel = () => {
    setShowDiscardDialog(true)
  }

  const handleConfirmDiscard = () => {
    setShowDiscardDialog(false)
    navigate(donorsPath, { state: { ...driveNavState, accountId } })
  }

  const buildSendPayload = () => ({
    donor_ids: donorIds,
    guest_ids: guestIds,
    source_tab: sourceTab,
    source: DRIVE_DONORS_EMAIL_SOURCE,
    recipient_mode: recipientMode,
    template_id: String(templateId),
    email_mode: emailMode,
    ...(isPreconfigured
      ? {}
      : {
          subject,
          email_body: emailBody,
        }),
  })

  const handleSend = async () => {
    setSending(true)
    setSendError('')
    try {
      const response = await chairpersonService.sendDriveDonorsEmail(
        driveId,
        accountId,
        buildSendPayload()
      )

      if (response?.status_code === 200) {
        const result = response.data
        const sentCount = Number(result?.sent_count) || 0

        if (sentCount > 0) {
          setSendResult(result)
          setShowSuccessDialog(true)
        } else {
          setSendError(
            result?.failures?.[0]?.reason ??
              response?.response ??
              'Flow did not accept the email request.'
          )
        }
      } else {
        setSendError(response?.response ?? 'Failed to send drive donors email.')
      }
    } catch (error) {
      setSendError(error?.message ?? 'Failed to send drive donors email.')
    } finally {
      setSending(false)
    }
  }

  const handleSendTestEmail = async () => {
    if (!testEmail.trim()) {
      setSendError('Test email address is required.')
      return
    }

    setSendingTestEmail(true)
    setSendError('')
    try {
      const response = await chairpersonService.sendDriveDonorsTestEmail(
        driveId,
        accountId,
        {
          test_email: testEmail.trim(),
          source: DRIVE_DONORS_EMAIL_SOURCE,
          template_id: String(templateId),
          email_mode: emailMode,
          ...(isPreconfigured
            ? {}
            : {
                subject,
                email_body: emailBody,
              }),
        }
      )

      if (response?.status_code === 200) {
        setShowTestEmailDialog(false)
        setTestEmail('')
      } else {
        setSendError(response?.response ?? 'Failed to send test email.')
      }
    } catch (error) {
      setSendError(error?.message ?? 'Failed to send test email.')
    } finally {
      setSendingTestEmail(false)
    }
  }

  const handleSuccessClose = () => {
    setShowSuccessDialog(false)
    navigate(donorsPath, { state: { ...driveNavState, accountId } })
  }

  return (
    <Box>
      <CommonButton
        variant='text'
        startIcon={<ArrowBackIosNewOutlinedIcon sx={{ fontSize: 14 }} />}
        onClick={handleBack}
        sx={{ mb: 2 }}
      >
        Back to {isPreconfigured ? 'Select Template' : 'Compose'}
      </CommonButton>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent='space-between'
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Stack direction='row' spacing={2} alignItems='center'>
          <EmailOutlinedIcon color='primary' sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant='h5' fontWeight={700}>
              Review & Send
            </Typography>
            {accountName ? (
              <Typography variant='body2' color='text.secondary'>
                {accountName}
              </Typography>
            ) : null}
          </Box>
        </Stack>
        <Stepper activeStep={activeStep} sx={{ minWidth: 280 }}>
          {stepLabels.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          bgcolor: 'grey.100',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
        }}
      >
        <Stack direction='row' spacing={2} alignItems='flex-start'>
          <GroupOutlinedIcon color='action' />
          <Box>
            <Typography variant='subtitle1' fontWeight={600}>
              {selectedCount} {recipientModeLabel} from{' '}
              {accountName || 'this account'}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {loadingPreview
                ? 'Loading recipient details...'
                : `${willReceive} will receive this email. ${willSkip} will be skipped.`}
            </Typography>
          </Box>
        </Stack>
        <CommonButton
          variant='secondary'
          onClick={() => setShowRecipientsDialog(true)}
          disabled={loadingPreview}
        >
          View Recipients
        </CommonButton>
      </Paper>

      <Stack spacing={2}>
        <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.paper' }}>
          {isPreconfigured ? (
            <>
              <Typography variant='subtitle2' color='text.secondary'>
                Email Type
              </Typography>
              <Typography variant='body1' sx={{ mb: 2 }}>
                Preconfigured email
              </Typography>

              <Typography variant='subtitle2' color='text.secondary'>
                Flow Email Template
              </Typography>
              <Typography variant='body1' sx={{ mb: 2 }}>
                {templateName || templateId}
              </Typography>

              <Alert severity='info'>
                Subject and body are managed in Flow. Recipient profile data and
                drive merge fields will be sent with each email.
              </Alert>
            </>
          ) : (
            <>
              <Typography variant='subtitle2' color='text.secondary'>
                Template
              </Typography>
              <Typography variant='body1' sx={{ mb: 2 }}>
                {templateName || templateId}
              </Typography>

              <Typography variant='subtitle2' color='text.secondary'>
                Subject
              </Typography>
              <Typography variant='body1' sx={{ mb: 2 }}>
                {subject}
              </Typography>

              <Typography variant='subtitle2' color='text.secondary'>
                Email Body Preview
              </Typography>
              <Box
                sx={{
                  mt: 1,
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'grey.50',
                }}
                dangerouslySetInnerHTML={{ __html: emailBody }}
              />
            </>
          )}
        </Paper>

        {sendError ? <Alert severity='error'>{sendError}</Alert> : null}
      </Stack>

      <Stack
        direction='row'
        justifyContent='space-between'
        spacing={2}
        sx={{ mt: 3 }}
      >
        <CommonButton
          variant='secondary'
          onClick={handleCancel}
          disabled={sending}
        >
          Cancel
        </CommonButton>
        <Stack direction='row' spacing={2}>
          <CommonButton
            variant='secondary'
            onClick={handleBack}
            disabled={sending}
          >
            Back
          </CommonButton>
          <CommonButton
            variant='secondary'
            onClick={() => setShowTestEmailDialog(true)}
            disabled={sending}
          >
            Send Test Email
          </CommonButton>
          <CommonButton
            variant='primary'
            onClick={handleSend}
            loading={sending}
            disabled={willReceive === 0}
          >
            Send Email
          </CommonButton>
        </Stack>
      </Stack>

      <DriveDonorsRecipientsDialog
        open={showRecipientsDialog}
        onClose={() => setShowRecipientsDialog(false)}
        recipients={preview?.recipients ?? []}
        skipped={preview?.skipped ?? []}
      />

      <CommonDialog
        open={showTestEmailDialog}
        onClose={() => !sendingTestEmail && setShowTestEmailDialog(false)}
        title='Send Test Email'
        actions={
          <>
            <CommonButton
              variant='secondary'
              onClick={() => setShowTestEmailDialog(false)}
              disabled={sendingTestEmail}
            >
              Cancel
            </CommonButton>
            <CommonButton
              variant='primary'
              onClick={handleSendTestEmail}
              loading={sendingTestEmail}
            >
              Send Test Email
            </CommonButton>
          </>
        }
      >
        <Box sx={{ px: 2, pb: 2 }}>
          <CommonTextField
            label='Test Email Address'
            value={testEmail}
            onChange={value => setTestEmail(value)}
            placeholder='person@example.com'
            fullWidth
          />
        </Box>
      </CommonDialog>

      <CommonDialog
        open={showSuccessDialog}
        onClose={handleSuccessClose}
        title='Email Sent'
      >
        <Typography variant='body1' sx={{ px: 2, pb: 2 }}>
          Successfully sent email to {sendResult?.sent_count ?? 0} donor
          {(sendResult?.sent_count ?? 0) === 1 ? '' : 's'}.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pb: 2 }}>
          <CommonButton variant='primary' onClick={handleSuccessClose}>
            Done
          </CommonButton>
        </Box>
      </CommonDialog>

      <CommonDialog
        open={showDiscardDialog}
        onClose={() => setShowDiscardDialog(false)}
        title='Cancel'
        actions={
          <>
            <CommonButton
              variant='secondary'
              onClick={() => setShowDiscardDialog(false)}
            >
              Keep editing
            </CommonButton>
            <CommonButton variant='primary' onClick={handleConfirmDiscard}>
              Discard
            </CommonButton>
          </>
        }
      >
        <Typography variant='body1' sx={{ px: 2, pb: 2 }}>
          Are you sure you want to cancel? Your draft will be lost.
        </Typography>
      </CommonDialog>
    </Box>
  )
}
